
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const initialized = useRef(false);
  const authListener = useRef<{ subscription: { unsubscribe: () => void } } | null>(null);

  const checkAdminStatus = useCallback(async (userId: string | undefined) => {
    if (!userId) {
      setIsAdmin(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Admin check error:', error);
        setIsAdmin(false);
        return;
      }

      setIsAdmin(data?.role === 'admin');
    } catch (error) {
      console.error('Admin check error:', error);
      setIsAdmin(false);
    }
  }, []);

  const updateAuthState = useCallback(async (currentSession: Session | null) => {
    if (!currentSession?.user) {
      setSession(null);
      setUser(null);
      setIsAdmin(false);
      setIsLoading(false);
      return;
    }

    setSession(currentSession);
    setUser(currentSession.user);
    await checkAdminStatus(currentSession.user.id);
    setIsLoading(false);
  }, [checkAdminStatus]);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const setupAuth = async () => {
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        await updateAuthState(initialSession);

        const { data } = supabase.auth.onAuthStateChange(async (_event, currentSession) => {
          await updateAuthState(currentSession);
        });

        authListener.current = data;
      } catch (error) {
        console.error('Auth setup error:', error);
        setIsLoading(false);
      }
    };

    setupAuth();

    return () => {
      if (authListener.current?.subscription) {
        authListener.current.subscription.unsubscribe();
      }
      authListener.current = null;
    };
  }, [updateAuthState]);

  const signIn = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      await updateAuthState(data.session);
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  };

  const signOut = async () => {
    setIsLoading(true);
    try {
      await supabase.auth.signOut();
      await updateAuthState(null);
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  };

  const value = {
    session,
    user,
    isAdmin,
    isLoading,
    signIn,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
